import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { mainAccountUserIdOf } from '@app/authentification/user-identity';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import * as _ from 'lodash';
import {
  combineLatest,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { UserService } from '../../providers/user/user.service';
import { Dao } from '../dao';
import { DataLoader } from '../data-loader';
import { DataLoaderAuthentified } from '../data-loader-authentified';

@Injectable({
  providedIn: 'root',
})
export class UserDao extends Dao {
  public onOwnersReset: Subject<void> = new Subject<void>();

  private couple: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
    return this.httpApi.get(`/user/${user.id}/relation/spouse`).pipe(
      switchMap((spouse: any) => {
        let couple: any[] = [user];
        if (spouse && spouse.data) couple.push(spouse.data.relationUser);
        return of(couple);
      }),
    );
  });

  private owners: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentified(), user => {
    return this.httpApi.get(`/accounts/${user.accountId}/owners`);
  });

  private fiscalSituation: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(
      this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(),
      user => {
        const currentYear = new Date().getFullYear();
        return this.httpApi.get(
          `/user/${user.id}/taxhouse/info/${currentYear}/global/openFisca`,
        );
      },
    );

  private children: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
    return this.userService
      .getSameTaxHouse(user.id)
      .pipe(
        map(res => _.filter(res['data'], relation => relation.type == 'CHILD')),
      );
  });

  private companies: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
    return this.userService.getCompanies(user.id);
  });

  private residencePrincipal: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(
      this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(),
      user => {
        let address = user.addresses[0];
        return this.getGoogleMapPrediction(address).pipe(
          map(googleResult => {
            return _.isNil(googleResult)
              ? {}
              : ({ ...address, googleResult: googleResult } as any);
          }),
        );
      },
    );

  private risk: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user =>
    forkJoin(of(user), this.httpApi.get(`/owner/${user.id}/risk-profile`)),
  );

  private coupleRisk: DataLoader<any[]> = new DataLoader<any[]>(() =>
    this.getCouple().pipe(
      mergeMap((couple: any) => {
        let subs = [of(couple)];
        subs.push(
          this.httpApi
            .get(`/owner/${couple[0].id}/risk-profile`)
            .pipe(tap(res => (res.accountId = couple[0].id))),
        );
        if (couple[1])
          subs.push(
            this.httpApi
              .get(`/owner/${couple[1].id}/risk-profile`)
              .pipe(tap(res => (res.accountId = couple[1].id))),
          );
        return forkJoin(subs);
      }),
      map(([couple, userRisk, partnerRisk]) => {
        couple[0].riskScore = userRisk ? userRisk.score : null;
        if (couple[1])
          couple[1].riskScore = partnerRisk ? partnerRisk.score : null;
        return [userRisk, partnerRisk || null];
      }),
    ),
  );

  private globalProducts: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(
      this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(),
      user => {
        return this.httpApi
          .get(`/user/${user.id}/globalProduct?source=INTERNAL`)
          .pipe(map(res => res.data));
      },
    );

  private taxInfoForCurrentYear: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(
      this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(),
      user => {
        return this.httpApi
          .get(`/user/${user.id}/taxhouse/info/${new Date().getFullYear()}`)
          .pipe(map(res => res.data));
      },
    );

  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
    private userService: UserService,
  ) {
    super(httpApi, authentificationService);
  }

  /* Couple */
  getCouple(): Observable<any[]> {
    return this.couple.get();
  }

  /* Owners */
  getOwners(): Observable<any[]> {
    return this.owners.get();
  }

  /* FiscalSituation */
  getCurrentFiscalSituation(): Observable<any[]> {
    return this.fiscalSituation.get();
  }

  /* Residence */
  getResidencePrincipal(reset?: boolean): Observable<any[]> {
    if (this.reset) this.residencePrincipal?.reset();
    return this.residencePrincipal.get();
  }

  /* Children */
  getChildren(): Observable<any[]> {
    return this.children.get();
  }

  resetChildren(): void {
    this.children?.reset();
  }

  resetCompanies(): void {
    this.companies?.reset();
  }

  getGlobalProducts(): Observable<any[]> {
    return this.globalProducts.get();
  }

  updateChild(entity, reset: boolean = true): Observable<any[]> {
    return this.commandAsMainUser(user =>
      this.userService.updateSameTaxHouse(user.id, entity),
    ).pipe(
      tap(() => {
        if (reset) {
          this.resetChildren();
          this.taxInfoForCurrentYear?.reset();
        }
      }),
    );
  }

  deleteChild(entity, reset: boolean = true): Observable<any[]> {
    return this.commandAsMainUser(user =>
      this.userService.deleteRelation(user.id, entity.id),
    ).pipe(
      tap(() => {
        if (reset) {
          this.resetChildren();
          this.taxInfoForCurrentYear?.reset();
        }
      }),
    );
  }

  /* Companies */
  getCompanies(): Observable<any[]> {
    return this.companies.get();
  }

  saveBeneficialOwner(
    entity,
    companyId: number,
    reset: boolean = true,
  ): Observable<any> {
    return this.commandAsMainUser(user =>
      this.httpApi.post(
        `/user/${user.id}/companies/${companyId}/beneficialOwners`,
        entity,
      ),
    ).pipe(
      tap(() => {
        if (reset) this.resetCompanies();
      }),
    );
  }

  removeBeneficialOwner = (id: number, companyId: number): Observable<any> => {
    return this.commandAsMainUser(user =>
      this.httpApi.delete(
        `/user/${user.id}/companies/${companyId}/beneficialOwners/${id}`,
      ),
    );
  };

  updateCompany(entity, reset: boolean = true): Observable<any> {
    if (!entity.id) {
      return this.commandAsMainUser(user =>
        this.httpApi.post(`/user/${user.id}/companies`, entity),
      ).pipe(
        tap(() => {
          if (reset) this.resetCompanies();
        }),
      );
    } else {
      return this.commandAsMainUser(user =>
        this.httpApi.put(`/user/${user.id}/companies/${entity.id}`, entity),
      ).pipe(
        tap(() => {
          if (reset) this.resetCompanies();
        }),
      );
    }
  }

  deleteCompany(entity, reset: boolean = true): Observable<any[]> {
    return this.commandAsMainUser(user =>
      this.userService.deleteCompany(user.id, entity.id),
    ).pipe(
      tap(() => {
        if (reset) this.resetCompanies();
      }),
    );
  }

  /* Risk */
  getRisk(): Observable<any[]> {
    return this.risk.get();
  }

  private riskByUser: Map<number, DataLoaderAuthentified<any>> = new Map();
  private esgByUser: Map<number, DataLoaderAuthentified<any>> = new Map();

  getRiskFor(userId: number): Observable<any> {
    this.assertValidOwnerId(userId);
    return this.getOrCreateRiskLoader(userId).get();
  }

  getEsgProfileFor(userId: number): Observable<any> {
    this.assertValidOwnerId(userId);
    return this.getOrCreateEsgLoader(userId).get();
  }

  private getOrCreateRiskLoader(userId: number): DataLoaderAuthentified<any> {
    let loader = this.riskByUser.get(userId);
    if (!loader) {
      loader = new DataLoaderAuthentified<any>(this.getAuthentified(), () =>
        this.httpApi.get(this.buildOwnerRiskPath(userId)),
      );
      this.riskByUser.set(userId, loader);
    }
    return loader;
  }

  private getOrCreateEsgLoader(userId: number): DataLoaderAuthentified<any> {
    let loader = this.esgByUser.get(userId);
    if (!loader) {
      loader = new DataLoaderAuthentified<any>(this.getAuthentified(), () =>
        this.httpApi.get(this.buildOwnerEsgPath(userId)),
      );
      this.esgByUser.set(userId, loader);
    }
    return loader;
  }

  private buildOwnerRiskPath(userId: number): string {
    this.assertValidOwnerId(userId);
    return `/owner/${encodeURIComponent(userId)}/risk-profile`;
  }

  private buildOwnerEsgPath(userId: number): string {
    this.assertValidOwnerId(userId);
    return `/owner/${encodeURIComponent(userId)}/esg-profile`;
  }

  private assertValidOwnerId(userId: number): void {
    if (!Number.isInteger(userId) || userId <= 0) {
      throw new Error('UserDao: ownerId must be a positive integer');
    }
  }

  getCoupleRisk(): Observable<any[]> {
    return this.coupleRisk.get();
  }

  createOrUpdateOwnerRiskProfile(
    ownerId: number,
    data: any,
    reset: boolean = true,
  ): Observable<any> {
    return this.httpApi.post(`/owner/${ownerId}/risk-profile`, data).pipe(
      tap(() => {
        if (reset) {
          this.risk?.reset();
          this.coupleRisk?.reset();
          this.riskByUser.delete(ownerId);
        }
      }),
    );
  }

  getEsgProfile(): Observable<any> {
    return this.commandAsMainUser(user =>
      this.httpApi.get(`/owner/${user.id}/esg-profile`),
    );
  }

  saveEsgProfile(ownerId: number, data: any): Observable<any> {
    return this.httpApi.post(`/owner/${ownerId}/esg-profile`, data).pipe(
      tap(() => {
        this.esgByUser.delete(ownerId);
      }),
    );
  }

  updateUser(entity: any, reset: boolean = true): Observable<any> {
    return combineLatest([
      this.httpApi.post(`/user`, entity),
      this.updateContacts(entity.id, entity.contacts, false),
    ]).pipe(
      tap(() => {
        if (reset) this?.reset();
      }),
    );
  }

  updateContacts(user: any, contacts: any, reset: boolean = true) {
    return combineLatest(
      _.map(contacts, contact => {
        if (!contact.value || contact.value == null || !contact.erase)
          return of(null);
        return this.httpApi.post(`/user/${user}/contact`, contact);
      }),
    ).pipe(
      tap(() => {
        if (reset) this?.reset();
      }),
    );
  }

  updateHouse(entity: any, reset: boolean = true) {
    const userId = mainAccountUserIdOf(entity);
    const mainAddress = entity.addresses.find(address => address.isMain);
    return combineLatest([
      // TODO : check that wtih paul
      this.httpApi.post(`/user`, _.omit(entity, 'regimeData')),
      this.httpApi.post(`/user/${userId}/address`, mainAddress),
      this.httpApi.post(
        `/user/${userId}/taxhouse/info/${new Date().getFullYear()}`,
        entity.taxData,
      ),
    ]).pipe(
      mergeMap(() => this.updateMarriageRegime(entity, false)),
      tap(() => {
        if (reset) this?.reset();
      }),
    );
  }

  updateMarriageRegime(entity, reset: boolean = true) {
    let obs = this.httpApi
      .post(
        `/user/${entity.id}/relation/current/marriageRegime`,
        entity.regimeData,
      )
      .pipe(
        tap(() => {
          if (reset) this?.reset();
        }),
      );

    if (
      entity.maritalSituation != 'CIVILPARTNER' &&
      entity.maritalSituation != 'MARRIED'
    )
      obs = of([null]);

    return obs;
  }

  // Pas marié ou pacsé => pas de post /marriageRegime
  // Nouveau marié ou pacsé => post current/marriageRegime : type: "COMM_RED_ACQ"
  // Modif marié ou pacsé => recup id à l'ouverture =>  post current/marriageRegime : id: "10764485", isCurrent: true, type: "COMM_MEUB_ACQ"

  /* utils */
  getUserWithAccountId(accountId) {
    return this.owners
      .get()
      .pipe(map(owners => owners.find(owner => owner.accountId === accountId)));
  }

  getGoogleMapPrediction(address): Observable<any> {
    if (_.isNil(address)) return of(null);

    // Check if Google Maps API is loaded
    if (typeof google === 'undefined' || !google.maps) {
      console.error('Google Maps API is not loaded yet');
      return of(null);
    }

    return Observable.create(obs => {
      let displaySuggestions = function (predictions, status) {
        if (status != google.maps.places.PlacesServiceStatus.OK) {
          obs.next(null);
          obs.complete();
        } else {
          geocoder.geocode({ placeId: predictions[0].place_id }, results => {
            if (results[0]) {
              obs.next(results[0]);
              obs.complete();
            } else {
              obs.next(null);
              obs.complete();
            }
          });
        }
      };

      const geocoder = new google.maps.Geocoder();
      var service = new google.maps.places.AutocompleteService();
      typeof address == 'string'
        ? service.getQueryPredictions({ input: address }, displaySuggestions)
        : service.getPlacePredictions(
            {
              input: `${address.street || ''} ${address.city || ''} ${
                address.zipCode || ''
              } ${address.country || ''}`,
            },
            displaySuggestions,
          );
    });
  }

  getTaxInfoForCurrentYear(): Observable<any> {
    return this.taxInfoForCurrentYear.get();
  }

  /* state */
  onUserChange(event: any) {
    this.reset();
  }

  reset() {
    this.couple?.reset();
    this.owners?.reset();
    this.resetChildren();
    this.risk?.reset();
    this.coupleRisk?.reset();
    this.resetCompanies();
    this.residencePrincipal?.reset();
    this.taxInfoForCurrentYear?.reset();
    this.riskByUser?.clear();
    this.esgByUser?.clear();
    this.onOwnersReset?.next();
  }
}
